import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Send, Users, Megaphone, History, AlertCircle } from 'lucide-react';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const SUBJECT_MAX = 100;
const MESSAGE_MAX = 2000;

const STATUS_VARIANT = { sent: 'default', partial: 'secondary', failed: 'destructive' };

export default function BroadcastMessages() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [recipientCount, setRecipientCount] = useState(0);
  const [history, setHistory] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);

  // Fetch eligible events
  const fetchEvents = useCallback(async () => {
    setLoadingEvents(true);
    try {
      const res = await axios.get(`${API_URL}/api/organizer/broadcast-eligible-events`);
      setEvents(res.data.events || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load events');
    } finally {
      setLoadingEvents(false);
    }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // Fetch recipient count + history when event changes
  useEffect(() => {
    if (!selectedEventId) {
      setRecipientCount(0);
      setHistory([]);
      return;
    }
    (async () => {
      try {
        const [rc, hist] = await Promise.all([
          axios.get(`${API_URL}/api/organizer/events/${selectedEventId}/broadcast-recipients`),
          axios.get(`${API_URL}/api/organizer/events/${selectedEventId}/broadcasts`),
        ]);
        setRecipientCount(rc.data.recipient_count || 0);
        setHistory(hist.data.broadcasts || []);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load event details');
      } finally {
        setLoadingHistory(false);
      }
    })();
  }, [selectedEventId]);

  const canSend = selectedEventId && recipientCount > 0 && subject.trim() && message.trim() && !sending;

  const onSendClick = () => {
    if (!subject.trim() || subject.length > SUBJECT_MAX) {
      toast.error(`Subject is required (≤${SUBJECT_MAX} chars)`);
      return;
    }
    if (!message.trim() || message.length > MESSAGE_MAX) {
      toast.error(`Message is required (≤${MESSAGE_MAX} chars)`);
      return;
    }
    if (recipientCount === 0) {
      toast.error('No confirmed attendees to message');
      return;
    }
    setConfirmOpen(true);
  };

  const submitBroadcast = async () => {
    setSending(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/organizer/events/${selectedEventId}/broadcast`,
        { subject: subject.trim(), message: message.trim() }
      );
      toast.success(`Broadcast sent to ${res.data.recipientCount} attendees`);
      setSubject('');
      setMessage('');
      setConfirmOpen(false);
      // Refresh history
      const hist = await axios.get(`${API_URL}/api/organizer/events/${selectedEventId}/broadcasts`);
      setHistory(hist.data.broadcasts || []);
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.detail || 'Failed to send broadcast';
      toast.error(status === 429 ? msg : msg);
      setConfirmOpen(false);
    } finally {
      setSending(false);
    }
  };

  const selectedEvent = events.find(e => e.event_id === selectedEventId);

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="mb-2" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Dashboard
        </Button>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
              <Megaphone className="w-7 h-7" /> Message Attendees
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Send a one-way broadcast email to all confirmed buyers of an event.
            </p>
          </div>
        </div>

        {loadingEvents ? (
          <p className="text-center py-8 text-muted-foreground">Loading your events…</p>
        ) : events.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center space-y-3" data-testid="no-eligible-events">
              <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground opacity-50" />
              <p className="text-base">No events with confirmed buyers yet.</p>
              <p className="text-sm text-muted-foreground">
                Once at least one person buys a ticket to an event you own, you'll be able to message them here.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Compose Broadcast</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="event">Event</Label>
                  <Select value={selectedEventId} onValueChange={setSelectedEventId}>
                    <SelectTrigger id="event" data-testid="broadcast-event-select">
                      <SelectValue placeholder="Select an event" />
                    </SelectTrigger>
                    <SelectContent>
                      {events.map(e => (
                        <SelectItem key={e.event_id} value={e.event_id} data-testid={`event-option-${e.event_id}`}>
                          {e.title} — {e.confirmed_ticket_count} ticket{e.confirmed_ticket_count === 1 ? '' : 's'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedEventId && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-muted/50">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm" data-testid="recipient-count">
                      <strong>{recipientCount}</strong> attendee{recipientCount === 1 ? '' : 's'} will receive this message
                    </span>
                  </div>
                )}

                <div>
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value.slice(0, SUBJECT_MAX))}
                    placeholder="A quick update about the venue"
                    data-testid="broadcast-subject"
                    maxLength={SUBJECT_MAX}
                  />
                  <p className="text-xs text-muted-foreground mt-1 text-right">{subject.length}/{SUBJECT_MAX}</p>
                </div>

                <div>
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value.slice(0, MESSAGE_MAX))}
                    rows={8}
                    placeholder="Hi everyone! Just a friendly reminder that doors open at 7pm sharp…"
                    data-testid="broadcast-message"
                    maxLength={MESSAGE_MAX}
                  />
                  <p className="text-xs text-muted-foreground mt-1 text-right">{message.length}/{MESSAGE_MAX}</p>
                </div>

                <div className="flex justify-end pt-2">
                  <Button onClick={onSendClick} disabled={!canSend} data-testid="broadcast-send-btn">
                    <Send className="w-4 h-4 mr-1" /> Send Broadcast
                  </Button>
                </div>
              </CardContent>
            </Card>

            {selectedEventId && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <History className="w-5 h-5" /> Broadcast History
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingHistory ? (
                    <p className="text-sm text-muted-foreground text-center py-6">Loading…</p>
                  ) : history.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-6">No broadcasts sent yet for this event.</p>
                  ) : (
                    <div className="border rounded-lg overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left p-3 font-medium">Date</th>
                            <th className="text-left p-3 font-medium">Subject</th>
                            <th className="text-right p-3 font-medium">Recipients</th>
                            <th className="text-center p-3 font-medium">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {history.map(b => (
                            <tr key={b.broadcast_id} data-testid={`broadcast-row-${b.broadcast_id}`}>
                              <td className="p-3 text-xs">{new Date(b.sent_at).toLocaleString()}</td>
                              <td className="p-3">{b.subject}</td>
                              <td className="p-3 text-right">{b.sent_count}/{b.recipient_count}</td>
                              <td className="p-3 text-center">
                                <Badge variant={STATUS_VARIANT[b.status] || 'outline'}>{b.status}</Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Confirmation Modal */}
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm broadcast</DialogTitle>
              <DialogDescription>
                You are about to send <strong>"{subject}"</strong> to <strong>{recipientCount}</strong> attendee{recipientCount === 1 ? '' : 's'} of <strong>{selectedEvent?.title || 'this event'}</strong>. This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setConfirmOpen(false)} disabled={sending} data-testid="broadcast-cancel-btn">Cancel</Button>
              <Button onClick={submitBroadcast} disabled={sending} data-testid="broadcast-confirm-btn">
                {sending ? 'Sending…' : `Send to ${recipientCount}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
